import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  cardPermissionIsInherent,
  formatDate,
  USER_ROLE_LABELS,
  type CatalogKind,
  type CompanyProfile,
  type PublicUser,
  type UserRole,
} from '@fsm/shared';
import { api } from '../lib/api';
import { SeatBilling, SeatCostNote } from '../components/SeatBilling';
import { PhoneCard } from '../components/PhoneCard';
import { keys } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { PaymentsCard } from '../components/PaymentsCard';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Toast,
  cx,
} from '../components/ui';

/**
 * Settings, which today means the mailbox.
 *
 * The screen's real job is not the Connect button — it is answering "why did
 * that email not arrive?" without anyone reading a log. So it shows what is
 * connected, when it last sent, what the last failure was, and where mail goes
 * when nothing is connected at all.
 */
type TabId = 'company' | 'email' | 'payments' | 'phone' | 'team' | 'lists';

const TABS: Array<{ id: TabId; label: string; hint: string }> = [
  { id: 'company', label: 'Company information', hint: 'What prints on documents' },
  { id: 'email', label: 'Email', hint: 'Where invoices send from' },
  { id: 'payments', label: 'Card payments', hint: 'How customers pay you' },
  { id: 'phone', label: 'Phone', hint: 'Your business number' },
  { id: 'team', label: 'Team', hint: 'Who can sign in' },
  { id: 'lists', label: 'Job types & sources', hint: 'The lists you pick from' },
];

export function SettingsPage() {
  const qc = useQueryClient();
  const { can, company } = useAuth();
  const isAdmin = can('admin');
  // The letterhead is the office's to keep current — a dispatcher correcting a
  // phone number on an invoice should not need an admin. Connecting a mailbox
  // stays admin-only: that one hands the whole company a sending identity.
  const isOffice = can('admin', 'dispatcher');
  /*
   * The open tab lives in the URL, so "the lead sources screen" is a link
   * somebody can send, and the back button steps between tabs the way it
   * looks like it should.
   */
  const [search, setSearch] = useSearchParams();
  const requested = search.get('tab');

  /*
   * The phone tab only exists for a company that has one. Filtered rather
   * than hidden in the markup so a `?tab=phone` URL — a bookmark from before
   * the feature was switched off, say — falls back to Company information
   * instead of rendering a card whose every request the server will refuse.
   */
  const tabs = TABS.filter((t) => t.id !== 'phone' || company?.telephonyEnabled === true);
  const tab: TabId = tabs.some((t) => t.id === requested) ? (requested as TabId) : 'company';
  const setTab = (next: TabId) => setSearch(next === 'company' ? {} : { tab: next });
  const [toast, setToast] = useState<{ message: string; tone: 'error' | 'success' } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: keys.mailSettings,
    queryFn: () => api.settings.email(),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: keys.mailSettings });

  const connect = useMutation({
    mutationFn: () => api.settings.connectGmail(),
    // Google's consent screen replaces this tab and sends the browser back to
    // /settings when it is done, so there is nothing to do on success.
    onSuccess: (result) => {
      window.location.href = result.url;
    },
    onError: (error: Error) => setToast({ message: error.message, tone: 'error' }),
  });

  const disconnect = useMutation({
    mutationFn: () => api.settings.disconnectMailbox(),
    onSuccess: () => {
      refresh();
      setToast({ message: 'Mailbox disconnected', tone: 'success' });
    },
    onError: (error: Error) => setToast({ message: error.message, tone: 'error' }),
  });

  const sendTest = useMutation({
    mutationFn: () => api.settings.sendTestEmail(),
    onSuccess: (result) => {
      refresh();
      setToast({
        message: result.ok
          ? result.isTestProvider
            ? `Written to the mail-out folder via ${result.via} — nothing was actually sent`
            : `Sent to ${result.to} via ${result.via}`
          : `Could not send: ${result.failureReason ?? 'unknown error'}`,
        tone: result.ok ? 'success' : 'error',
      });
    },
    onError: (error: Error) => setToast({ message: error.message, tone: 'error' }),
  });

  if (isLoading || !data) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-ink-4" />
      </div>
    );
  }

  const account = data.account;

  return (
    <>
      <PageHeader
        eyebrow="Business"
        title="Settings"
        subtitle="How this system talks to the outside world"
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="flex max-w-[1080px] items-start gap-6">
          {/*
           * A rail rather than a row of tabs across the top. Settings only
           * grows — a company profile, a mailbox, lead sources, and whatever
           * comes next — and a vertical list takes new entries without
           * reflowing or truncating the ones already there.
           */}
          <nav className="w-[200px] shrink-0 space-y-0.5">
            {tabs.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => setTab(entry.id)}
                className={cx(
                  'w-full rounded-lg px-3 py-2 text-left text-[13.5px] transition-colors',
                  tab === entry.id
                    ? 'bg-surface font-semibold text-ink shadow-[0_1px_2px_rgb(11_14_20/0.06),0_0_0_1px_rgb(11_14_20/0.07)]'
                    : 'font-medium text-ink-3 hover:bg-surface/70 hover:text-ink-2',
                )}
              >
                {entry.label}
                <span className="mt-0.5 block text-[11.5px] font-normal text-ink-4">
                  {entry.hint}
                </span>
              </button>
            ))}
          </nav>

          <div className="min-w-0 flex-1 space-y-5">
        {tab === 'company' && (
          <CompanyProfileCard
            readOnly={!isOffice}
            onSaved={() => setToast({ message: 'Company details saved', tone: 'success' })}
            onError={(message) => setToast({ message, tone: 'error' })}
          />
        )}

        {tab === 'lists' && (
          <>
            <CatalogCard
              kind="job_type"
              title="Job types"
              blurb="What kind of work a job is. Offered on every job, and the way to ask later which kind actually pays."
              placeholder="Drain clearing, Install, Warranty…"
              readOnly={!isOffice}
            />
            <CatalogCard
              kind="lead_source"
              title="Lead sources"
              blurb="How work reaches you. Anyone in the office can add one while they are on the phone — this screen is for tidying up afterwards."
              placeholder="Van sign, Nextdoor, Referral…"
              readOnly={!isOffice}
            />
          </>
        )}

        {tab === 'payments' && <PaymentsCard canManage={isAdmin} />}
        {tab === 'phone' && <PhoneCard canManage={isAdmin} />}

        {tab === 'team' && <TeamCard canManage={isAdmin} />}

        {tab === 'email' && (
          <Card
            title="Sending email"
            action={
              account && isAdmin ? (
                <Button
                  size="sm"
                  loading={disconnect.isPending}
                  onClick={() => disconnect.mutate()}
                >
                  Disconnect
                </Button>
              ) : null
            }
          >
            {account ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-[15px] font-semibold">{account.emailAddress}</span>
                  <span className="text-[12.5px] text-ink-4">
                    connected {formatDate(account.connectedAt)}
                    {account.connectedByName ? ` by ${account.connectedByName}` : ''}
                  </span>
                </div>
                <p className="mt-1.5 text-[13px] text-ink-2">
                  Invoices, estimates and receipts are sent from this address, and a copy of each
                  one appears in its Sent folder.
                </p>

                <dl className="mt-3 space-y-1.5 border-t border-line pt-3 text-[13px]">
                  <div className="flex justify-between">
                    <dt className="text-ink-3">Last sent</dt>
                    <dd className="tnum">
                      {account.lastSentAt ? formatDate(account.lastSentAt) : 'nothing yet'}
                    </dd>
                  </div>
                </dl>

                {account.lastError && (
                  <div className="mt-3 rounded-lg border border-line bg-canvas px-3 py-2.5 text-[13px]">
                    <div className="font-semibold text-ink">Last attempt failed</div>
                    <p className="mt-0.5 text-ink-2">{account.lastError}</p>
                    {account.lastErrorAt && (
                      <p className="mt-1 text-[12px] text-ink-4">
                        {formatDate(account.lastErrorAt)}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-[13.5px] text-ink-2">
                  No mailbox is connected, so mail goes out through{' '}
                  <span className="font-medium text-ink">{data.fallbackProvider.displayName}</span>
                  {data.fallbackProvider.isTest
                    ? ' — which writes messages to a folder on the server and sends nothing.'
                    : '.'}
                </p>
                <p className="mt-2 text-[13px] text-ink-3">
                  Connecting a Google account lets invoices and estimates go from your own address,
                  with replies landing in your own inbox. This system is only ever allowed to send —
                  it cannot read, search or delete anything in the mailbox.
                </p>

                {isAdmin ? (
                  <div className="mt-4">
                    <Button
                      variant="primary"
                      loading={connect.isPending}
                      disabled={!data.canConnect}
                      onClick={() => connect.mutate()}
                    >
                      Connect Gmail
                    </Button>
                    {!data.canConnect && (
                      <p className="mt-2 text-[12.5px] text-ink-3">
                        {!data.googleConfigured
                          ? 'This server has no Google client configured yet.'
                          : 'This server has no MAIL_TOKEN_KEY, so the connection could not be stored safely.'}{' '}
                        See docs/gmail-connect-setup.md.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 text-[12.5px] text-ink-3">
                    An admin can connect a mailbox here.
                  </p>
                )}
              </>
            )}

            {isAdmin && (
              <div className="mt-4 border-t border-line pt-3">
                <Button size="sm" loading={sendTest.isPending} onClick={() => sendTest.mutate()}>
                  Send test email
                </Button>
                <span className="ml-2 text-[12.5px] text-ink-4">
                  Goes to your own address, so nothing reaches a customer.
                </span>
              </div>
            )}
          </Card>
        )}

          {tab === 'email' && isAdmin && !account && data.googleConfigured && (
            <Card title="For the Google console">
              <p className="text-[13px] text-ink-2">
                The redirect URI this server expects. It has to match what is in the Google Cloud
                console character for character, or Google refuses the connection.
              </p>
              <code className="mt-2 block rounded-lg border border-line bg-canvas px-3 py-2 text-[12.5px] break-all">
                {data.redirectUri}
              </code>
            </Card>
          )}
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

/**
 * Who this company is, on every document a customer sees.
 *
 * This is the only place these values are entered. The printed invoice, the
 * printed estimate and the emails all build their header from the same record
 * through one `letterhead()` on the server — so a licence number typed here
 * appears on all of them, and a company that never fills one in never sees an
 * empty label.
 */
function CompanyProfileCard({
  readOnly,
  onSaved,
  onError,
}: {
  readOnly: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Partial<CompanyProfile> | null>(null);

  const { data: company, isLoading } = useQuery({
    queryKey: keys.companyProfile,
    queryFn: () => api.settings.company(),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: keys.companyProfile });

  const save = useMutation({
    mutationFn: () =>
      api.settings.updateCompany({
        name: draft?.name ?? company!.name,
        email: draft?.email ?? company!.email,
        phone: draft?.phone ?? company!.phone,
        website: draft?.website ?? company!.website,
        licenseNumber: draft?.licenseNumber ?? company!.licenseNumber,
        addressL1: draft?.addressL1 ?? company!.addressL1,
        addressL2: draft?.addressL2 ?? company!.addressL2,
        city: draft?.city ?? company!.city,
        state: draft?.state ?? company!.state,
        postalCode: draft?.postalCode ?? company!.postalCode,
        currency: draft?.currency ?? company!.currency,
        taxRateBps: draft?.taxRateBps ?? company!.taxRateBps,
      }),
    onSuccess: () => {
      setDraft(null);
      refresh();
      onSaved();
    },
    onError: (error: Error) => onError(error.message),
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.settings.uploadLogo(file, file.name),
    onSuccess: refresh,
    onError: (error: Error) => onError(error.message),
  });

  const removeLogo = useMutation({
    mutationFn: () => api.settings.removeLogo(),
    onSuccess: refresh,
    onError: (error: Error) => onError(error.message),
  });

  if (isLoading || !company) {
    return (
      <Card title="Company details">
        <Spinner className="size-5 text-ink-4" />
      </Card>
    );
  }

  const editing = draft !== null;
  const value = (key: keyof CompanyProfile) =>
    (draft?.[key] as string | null | undefined) ?? (company[key] as string | null) ?? '';
  const set = (key: keyof CompanyProfile) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...(d ?? {}), [key]: e.target.value }));

  return (
    <Card
      title="Company details"
      action={
        !readOnly &&
        (editing ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        ) : (
          <Button size="sm" onClick={() => setDraft({})}>
            Edit
          </Button>
        ))
      }
    >
      <p className="text-[13px] text-ink-3">
        What goes at the top of every invoice, estimate and email you send.
      </p>

      <div className="mt-4 flex items-start gap-4 border-b border-line pb-4">
        <div
          className={cx(
            'grid size-[72px] shrink-0 place-items-center overflow-hidden rounded-card',
            company.logoUrl ? 'bg-surface ring-1 ring-line ring-inset' : 'bg-canvas ring-1 ring-line-2 ring-inset',
          )}
        >
          {company.logoUrl ? (
            <img src={company.logoUrl} alt="" className="max-h-[72px] max-w-full object-contain" />
          ) : (
            <span className="text-[11px] text-ink-4">No logo</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium">Logo</div>
          <p className="mt-0.5 text-[12px] text-ink-4">
            PNG, JPEG or WebP. It prints about 50px tall, so a wide wordmark reads better than a
            tall badge.
          </p>
          {!readOnly && (
            <div className="mt-2 flex gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo.mutate(file);
                  e.target.value = '';
                }}
              />
              <Button size="sm" loading={uploadLogo.isPending} onClick={() => fileInput.current?.click()}>
                {company.logoUrl ? 'Replace' : 'Upload'}
              </Button>
              {company.logoUrl && (
                <Button
                  size="sm"
                  variant="danger"
                  loading={removeLogo.isPending}
                  onClick={() => removeLogo.mutate()}
                >
                  Remove
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-4 space-y-3">
          <Field label="Company name" required>
            <Input value={value('name')} onChange={set('name')} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <Input value={value('phone')} onChange={set('phone')} />
            </Field>
            <Field label="Email">
              <Input value={value('email')} onChange={set('email')} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Website">
              <Input value={value('website')} onChange={set('website')} placeholder="quarrybend.com" />
            </Field>
            <Field label="Licence number" hint="printed on every document">
              <Input value={value('licenseNumber')} onChange={set('licenseNumber')} />
            </Field>
          </div>
          <Field label="Street address">
            <Input value={value('addressL1')} onChange={set('addressL1')} />
          </Field>
          <Field label="Suite, unit" hint="optional">
            <Input value={value('addressL2')} onChange={set('addressL2')} />
          </Field>
          <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
            <Field label="City">
              <Input value={value('city')} onChange={set('city')} />
            </Field>
            <Field label="State">
              <Input value={value('state')} onChange={set('state')} />
            </Field>
            <Field label="ZIP">
              <Input value={value('postalCode')} onChange={set('postalCode')} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Currency"
              hint="three-letter code — every amount is shown in it"
            >
              <Input
                value={(draft?.currency ?? company.currency) || ''}
                onChange={(e) =>
                  setDraft((d) => ({ ...(d ?? {}), currency: e.target.value.toUpperCase() }))
                }
                maxLength={3}
                className="tnum uppercase"
              />
            </Field>
            <Field label="Sales tax" hint="default rate on new documents">
              <Input
                value={((draft?.taxRateBps ?? company.taxRateBps) / 100).toString()}
                onChange={(e) => {
                  const pct = Number(e.target.value);
                  setDraft((d) => ({
                    ...(d ?? {}),
                    taxRateBps: Number.isFinite(pct) ? Math.round(pct * 100) : company.taxRateBps,
                  }));
                }}
                inputMode="decimal"
                className="tnum"
              />
            </Field>
          </div>
        </div>
      ) : (
        <dl className="mt-4 space-y-2 text-[13px]">
          <ProfileRow label="Name" value={company.name} />
          <ProfileRow
            label="Address"
            value={
              [company.addressL1, company.addressL2, company.city, company.state, company.postalCode]
                .filter(Boolean)
                .join(', ') || null
            }
          />
          <ProfileRow label="Phone" value={company.phone} />
          <ProfileRow label="Email" value={company.email} />
          <ProfileRow label="Website" value={company.website} />
          <ProfileRow label="Licence" value={company.licenseNumber} />
          <ProfileRow label="Currency" value={company.currency} />
          <ProfileRow label="Sales tax" value={`${(company.taxRateBps / 100).toFixed(2)}%`} />
        </dl>
      )}
    </Card>
  );
}

function ProfileRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-ink-4">{label}</dt>
      <dd className={cx('min-w-0 truncate text-right', !value && 'text-ink-4')}>
        {value || 'Not set'}
      </dd>
    </div>
  );
}

/**
 * One of the office's own lists.
 *
 * Removing archives rather than deletes: the hundred jobs already tagged
 * "Valpak" still have to be able to say so when somebody asks what the mailer
 * brought in. Removing only stops it being offered on new records.
 */
function CatalogCard({
  kind,
  title,
  blurb,
  placeholder,
  readOnly,
}: {
  kind: CatalogKind;
  title: string;
  blurb: string;
  placeholder: string;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const { data: sources = [], isLoading } = useQuery({
    queryKey: [...keys.catalog(kind), 'all'],
    queryFn: () => api.catalog.list(kind, true),
    select: (d) => d.items,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['catalog'] });

  const add = useMutation({
    mutationFn: () => api.catalog.create({ kind, name: name.trim() }),
    onSuccess: () => {
      setName('');
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.catalog.remove(id),
    onSuccess: refresh,
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.catalog.restore(id),
    onSuccess: refresh,
  });

  const active = sources.filter((s) => !s.archivedAt);
  const retired = sources.filter((s) => s.archivedAt);
  const shown = showRetired ? sources : active;

  return (
    <Card title={title}>
      <p className="text-[13px] text-ink-3">{blurb}</p>

      {!readOnly && (
        <div className="mt-4 flex gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) add.mutate();
            }}
          />
          <Button
            variant="primary"
            disabled={!name.trim()}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            Add
          </Button>
        </div>
      )}

      {error && <p className="mt-2 text-[12.5px] text-danger">{error}</p>}

      {isLoading ? (
        <Spinner className="mt-4 size-5 text-ink-4" />
      ) : shown.length === 0 ? (
        <p className="mt-4 py-4 text-center text-[13px] text-ink-4">
          Nothing on the list yet. Add the two or three you already know about.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-line border-t border-line">
          {shown.map((source) => (
            <li
              key={source.id}
              className={cx('flex items-center gap-3 py-2.5', source.archivedAt && 'opacity-60')}
            >
              <span className="min-w-0 flex-1 truncate text-[13.5px]">{source.name}</span>
              {source.useCount !== undefined && (
                <span className="shrink-0 text-[12px] tnum text-ink-4">
                  {source.useCount} {source.useCount === 1 ? 'record' : 'records'}
                </span>
              )}
              {!readOnly &&
                (source.archivedAt ? (
                  <Button size="sm" onClick={() => restore.mutate(source.id)}>
                    Restore
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => archive.mutate(source.id)}>
                    Remove
                  </Button>
                ))}
            </li>
          ))}
        </ul>
      )}

      {retired.length > 0 && (
        <button
          type="button"
          onClick={() => setShowRetired((v) => !v)}
          className="mt-3 text-[12.5px] text-accent hover:underline"
        >
          {showRetired ? 'Hide' : `Show ${retired.length} removed`}
        </button>
      )}
    </Card>
  );
}

/**
 * Who can sign in.
 *
 * Two rules the server also enforces, stated here so the screen never offers
 * a button that will fail: somebody has to stay an admin, and you cannot
 * deactivate yourself. People are deactivated rather than deleted — a former
 * technician's name is on two years of job history.
 */
function TeamCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: team = [], isLoading } = useQuery({
    queryKey: keys.users('everyone'),
    queryFn: () => api.users.list({ includeInactive: true }),
    select: (d) => d.items,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['users'] });

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) => api.users.update(id, { role }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Letting a technician take a card at the door.
   *
   * Per person, not per company: the lead on a van takes payment and the
   * apprentice riding along does not. Office roles are not shown this at all —
   * they have it by virtue of the role, and a switch that appeared to turn it
   * off for an admin would be a lie. The rule itself is in the shared core so
   * this screen, the field app and the server cannot drift apart.
   */
  const setCardPermission = useMutation({
    mutationFn: ({ id, allowed }: { id: string; allowed: boolean }) =>
      api.users.update(id, { canTakePayment: allowed }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Shown only for office roles. A technician is always on the board, so a
   * switch beside one would be a control that does nothing — the same reason
   * the card permission hides for roles that carry it inherently.
   */
  const setGoesOnJobs = useMutation({
    mutationFn: ({ id, goes }: { id: string; goes: boolean }) =>
      api.users.update(id, { goesOnJobs: goes }),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.users.deactivate(id),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => api.users.reactivate(id),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const activeAdmins = team.filter((u) => u.role === 'admin' && u.active).length;

  return (
    <>
      <Card
        title={`Team (${team.filter((u) => u.active).length})`}
        action={
          canManage &&
          !adding && (
            <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
              Add someone
            </Button>
          )
        }
        bodyClassName="p-0"
      >
        <SeatBilling activeUsers={team.filter((u) => u.active).length} canManage={canManage} />

        <p className="px-4 pt-3 text-[13px] text-ink-3">
          Everyone here can sign in. Technicians see only their own jobs; the office sees
          everything.
        </p>

        {error && (
          <p className="mx-4 mt-3 rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">
            {error}
          </p>
        )}

        {isLoading ? (
          <div className="grid place-items-center py-8">
            <Spinner className="size-5 text-ink-4" />
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-line border-t border-line">
            {team.map((member: PublicUser) => {
              const isMe = member.id === me?.id;
              // The server refuses both of these; the screen should not offer them.
              const lastAdmin = member.role === 'admin' && member.active && activeAdmins === 1;

              return (
                <li
                  key={member.id}
                  className={cx('flex items-center gap-3 px-4 py-3', !member.active && 'opacity-55')}
                >
                  <Avatar name={member.name} color={member.colorHex} size={32} />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-[13.5px] font-medium">{member.name}</span>
                      {isMe && <span className="text-[11.5px] text-ink-4">you</span>}
                      {!member.active && <Badge tone="slate">Deactivated</Badge>}
                    </div>
                    <div className="truncate text-[12px] text-ink-4">{member.email}</div>

                    {canManage && member.active && !cardPermissionIsInherent(member.role) && (
                      <label className="mt-1 flex w-fit items-center gap-1.5 text-[12px] text-ink-3">
                        <input
                          type="checkbox"
                          checked={member.canTakePayment}
                          disabled={setCardPermission.isPending}
                          onChange={(e) =>
                            setCardPermission.mutate({
                              id: member.id,
                              allowed: e.target.checked,
                            })
                          }
                          className="size-3.5 accent-accent"
                        />
                        Can take card payments in the field app
                      </label>
                    )}

                    {canManage && member.active && member.role !== 'tech' && (
                      <label className="mt-1 flex w-fit items-center gap-1.5 text-[12px] text-ink-3">
                        <input
                          type="checkbox"
                          checked={member.goesOnJobs}
                          disabled={setGoesOnJobs.isPending}
                          onChange={(e) =>
                            setGoesOnJobs.mutate({ id: member.id, goes: e.target.checked })
                          }
                          className="size-3.5 accent-accent"
                        />
                        Goes on jobs — can be assigned work and gets a column on dispatch
                      </label>
                    )}
                  </div>

                  {canManage && member.active ? (
                    // Wrapped rather than sized by a class: the Select's own
                    // base styles carry `w-full`, and which of two width
                    // utilities wins is decided by stylesheet order, not by
                    // the order they appear in the attribute.
                    <div className="w-[150px] shrink-0">
                    <Select
                      className="h-8 text-[13px]"
                      value={member.role}
                      disabled={lastAdmin || setRole.isPending}
                      title={lastAdmin ? 'Somebody has to stay an admin' : undefined}
                      onChange={(e) =>
                        setRole.mutate({ id: member.id, role: e.target.value as UserRole })
                      }
                    >
                      {(['admin', 'dispatcher', 'tech'] as UserRole[]).map((role) => (
                        <option key={role} value={role}>
                          {USER_ROLE_LABELS[role]}
                        </option>
                      ))}
                    </Select>
                    </div>
                  ) : (
                    <span className="text-[12.5px] text-ink-3">
                      {USER_ROLE_LABELS[member.role]}
                    </span>
                  )}

                  {canManage &&
                    (member.active ? (
                      <Button
                        size="sm"
                        disabled={isMe || lastAdmin}
                        title={
                          isMe
                            ? 'You cannot deactivate your own account'
                            : lastAdmin
                              ? 'Somebody has to stay an admin'
                              : undefined
                        }
                        onClick={() => deactivate.mutate(member.id)}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => reactivate.mutate(member.id)}>
                        Restore
                      </Button>
                    ))}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {adding && (
        <AddTeamMemberModal
          activeUsers={team.filter((u) => u.active).length}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

function AddTeamMemberModal({
  activeUsers,
  onClose,
  onAdded,
}: {
  activeUsers: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'tech' as UserRole,
    phone: '',
    extension: '',
    colorHex: '#2563eb',
    canTakePayment: false,
    goesOnJobs: false,
    password: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.users.create({
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        phone: form.phone.trim() || null,
        extension: form.extension.trim() || null,
        colorHex: form.role === 'tech' ? form.colorHex : null,
        /*
         * Only sent for a technician. An office role has the permission
         * already, and sending false for one would store a column that says
         * the opposite of what the rule does.
         */
        canTakePayment: form.role === 'tech' ? form.canTakePayment : undefined,
        /* Meaningless for a technician, who is always on the board. */
        goesOnJobs: form.role === 'tech' ? undefined : form.goesOnJobs,
        password: form.password,
      }),
    onSuccess: onAdded,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not add them'),
  });

  const canSubmit =
    form.name.trim() && form.email.trim() && form.password.length >= 10;

  return (
    <Modal
      open
      onClose={onClose}
      title="Add someone to the team"
      description="They sign in with this address and password, and can change the password afterwards."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/*
         * Before the fields, not after them. Somebody who learns the price
         * underneath a filled-in form has already decided; somebody who reads
         * it first is making a decision.
         */}
        <div className="rounded-lg border border-line bg-raised px-3 py-2.5">
          <SeatCostNote activeUsers={activeUsers} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </Field>
          <Field label="Role" required>
            <Select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
            >
              <option value="tech">Technician — their own jobs only</option>
              <option value="dispatcher">Dispatcher — the whole board</option>
              <option value="admin">Admin — everything, including settings</option>
            </Select>
          </Field>
        </div>

        <Field label="Email" required hint="this is what they sign in with">
          <Input
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            inputMode="email"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </Field>
          <Field label="Extension" hint="what click-to-call rings">
            <Input
              value={form.extension}
              onChange={(e) => setForm((f) => ({ ...f, extension: e.target.value }))}
            />
          </Field>
        </div>

        {form.role === 'tech' && (
          <>
            <Field label="Board colour" hint="their column on the dispatch board">
              <input
                type="color"
                value={form.colorHex}
                onChange={(e) => setForm((f) => ({ ...f, colorHex: e.target.value }))}
                className="h-9 w-16 rounded-lg border border-line-2 bg-surface p-1"
              />
            </Field>

            <label className="flex items-start gap-2 text-[13px] text-ink-2">
              <input
                type="checkbox"
                checked={form.canTakePayment}
                onChange={(e) => setForm((f) => ({ ...f, canTakePayment: e.target.checked }))}
                className="mt-0.5 size-4 accent-accent"
              />
              <span>
                Can take card payments in the field app
                <span className="block text-[12px] text-ink-4">
                  They can type a customer's card into their phone and charge it. Off unless you
                  turn it on, and you can change it later.
                </span>
              </span>
            </label>
          </>
        )}

        {form.role !== 'tech' && (
          <label className="flex items-start gap-2 text-[13px] text-ink-2">
            <input
              type="checkbox"
              checked={form.goesOnJobs}
              onChange={(e) => setForm((f) => ({ ...f, goesOnJobs: e.target.checked }))}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>
              Goes on jobs
              <span className="block text-[12px] text-ink-4">
                For an owner or dispatcher who also works in the field. They get a column on the
                dispatch board and can be assigned jobs, on top of everything their role already
                lets them do.
              </span>
            </span>
          </label>
        )}

        <Field
          label="Starting password"
          required
          hint="at least 10 characters — tell it to them in person, not by email"
        >
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />
        </Field>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}
